'use client';

import { useMemo } from 'react';
import { Key01Icon } from '@terra/ui-icons';
import { HStack } from '@terra/ui-styled-system';
import type { ToolExecutionHar } from '@terra/shared-contracts-tracker';
import { Text } from '../../../components/text';
import { Tooltip } from '../../../components/tooltip';
import { isMongoId } from '../trace-view/context/EntityResolverContext';
import { useMatchedCredentialFields } from './use-tool-har-matched-credential';

type ToolHarCredentialBadgeProps = {
  har: ToolExecutionHar;
  showSeparator?: boolean;
};

export function ToolHarCredentialBadge({
  har,
  showSeparator,
}: ToolHarCredentialBadgeProps) {
  const fields = useMatchedCredentialFields(har);
  const rawId = har.matchedCredentialId?.trim();
  const tooltipProps = useMemo(() => {
    if (!rawId || !isMongoId(rawId)) {
      return null;
    }

    if (fields?.role && fields?.username) {
      return {
        title: `Role: ${fields.role}`,
        subtitle: `Username: ${fields.username}`,
      };
    }

    if (fields?.role) {
      return { title: `Role: ${fields.role}` };
    }

    if (fields?.username) {
      return { title: `Username: ${fields.username}` };
    }

    return { title: `Credential: ${rawId.slice(0, 8)}…` };
  }, [rawId, fields]);

  if (!tooltipProps) {
    return null;
  }

  return (
    <HStack gap={1.5}>
      <Tooltip title={tooltipProps.title} subtitle={tooltipProps.subtitle}>
        <Key01Icon
          size={14}
          color="var(--colors-fg-muted)"
          aria-label={[tooltipProps.title, tooltipProps.subtitle]
            .filter(Boolean)
            .join(' ')}
        />
      </Tooltip>
      {showSeparator ? (
        <Text fontSize="xs" color="fg.muted">
          •
        </Text>
      ) : null}
    </HStack>
  );
}
